/**
 * Eigenbau: Gespeicherte Sichten — benannte Aufgabenfilter.
 *
 * Service, tRPC und MCP lagen seit Paket 6 bereit; die Oberfläche fehlte.
 * Hier lassen sich Sichten anlegen und auflisten. Anwenden bleibt an der
 * Aufgabenliste (Filter per Adresse), die Sicht speichert den Ausgangspunkt.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Leerzustand } from "@/components/Leerzustand";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Schalter } from "@/components/entwurf/Schalter";
import { api } from "@/lib/api";
import { useProjekte } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  Erfolgsmeldung,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
} from "./gemeinsam";

const ZUSTANDSGRUPPEN = [
  { id: "", label: "Alle Gruppen" },
  { id: "offen", label: "Offen" },
  { id: "in_arbeit", label: "In Arbeit" },
  { id: "abnahme", label: "Abnahme" },
  { id: "erledigt", label: "Erledigt" },
  { id: "verworfen", label: "Verworfen" },
] as const;

export function EinstellungenSichten() {
  const client = useQueryClient();
  const projekte = useProjekte();
  const [name, setzeName] = useState("");
  const [projektId, setzeProjektId] = useState("");
  const [zustandGruppe, setzeZustandGruppe] = useState("");
  const [nurOffen, setzeNurOffen] = useState(true);
  const [geteilt, setzeGeteilt] = useState(false);
  const [ok, setzeOk] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<Meldung | null>(null);

  const sichten = useQuery({
    queryKey: ["cubic", "sichten"],
    queryFn: () => api.sichten.liste.query(),
  });

  const anlegen = useMutation({
    mutationFn: () =>
      api.sichten.anlegen.mutate({
        name: name.trim(),
        filter: {
          ...(projektId ? { projektId } : {}),
          ...(zustandGruppe ? { zustandGruppe } : {}),
          nurOffen,
        },
        geteilt: geteilt && Boolean(projektId),
      }),
    onSuccess: async () => {
      setzeName("");
      setzeFehler(null);
      setzeOk("Sicht gespeichert.");
      await client.invalidateQueries({ queryKey: ["cubic", "sichten"] });
    },
    onError: (e) => {
      setzeOk(null);
      setzeFehler(meldungFuer(e, "Sicht konnte nicht angelegt werden."));
    },
  });

  const projektName = (id: string | null | undefined) => {
    if (!id) return "alle Projekte";
    return projekte.data?.find((p) => p.id === id)?.name ?? id.slice(0, 8);
  };

  return (
    <EinstellungenHuelle
      aktiv="sichten"
      beschreibung="Benannte Filter über Aufgaben — persönlich oder im Projekt geteilt."
    >
      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      <Erfolgsmeldung text={ok} />

      <EinstellungsAbschnitt
        titel="Neue Sicht"
        beschreibung="Speichert den aktuellen Filter. Geteilte Sichten brauchen ein Projekt."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="sicht-name">Name</Label>
            <Input
              id="sicht-name"
              className="mt-1.5"
              value={name}
              maxLength={120}
              placeholder="z. B. Meine Rückstände"
              onChange={(e) => {
                setzeName(e.target.value);
                setzeOk(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div>
            <Label htmlFor="sicht-projekt">Projekt</Label>
            <Select
              value={projektId || "__alle__"}
              onValueChange={(wert) => {
                setzeProjektId(wert === "__alle__" ? "" : wert);
                if (wert === "__alle__") setzeGeteilt(false);
              }}
            >
              <SelectTrigger id="sicht-projekt" className="mt-1.5" aria-label="Projekt">
                <SelectValue placeholder="Alle Projekte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__alle__">Alle Projekte</SelectItem>
                {(projekte.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.kuerzel} · {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="sicht-gruppe">Zustandsgruppe</Label>
            <Select
              value={zustandGruppe || "__alle__"}
              onValueChange={(wert) =>
                setzeZustandGruppe(wert === "__alle__" ? "" : wert)
              }
            >
              <SelectTrigger id="sicht-gruppe" className="mt-1.5" aria-label="Zustandsgruppe">
                <SelectValue placeholder="Alle Gruppen" />
              </SelectTrigger>
              <SelectContent>
                {ZUSTANDSGRUPPEN.map((g) => (
                  <SelectItem
                    key={g.id || "__alle__"}
                    value={g.id || "__alle__"}
                  >
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm text-tinte">
            <Schalter
              an={nurOffen}
              ariaLabel="Nur offene Aufgaben"
              onKlick={() => setzeNurOffen((v) => !v)}
            />
            Nur offene Aufgaben
          </label>
          <label className="flex items-center gap-2 text-sm text-tinte">
            <Schalter
              an={geteilt}
              ariaLabel="Im Projekt teilen"
              onKlick={() => {
                if (!projektId) return;
                setzeGeteilt((v) => !v);
              }}
            />
            Im Projekt teilen
            {!projektId ? (
              <span className="text-[11.5px] text-tinte3">
                (braucht Projekt)
              </span>
            ) : null}
          </label>
          <Button
            onClick={() => anlegen.mutate()}
            disabled={anlegen.isPending || name.trim().length === 0}
            isLoading={anlegen.isPending}
          >
            Speichern
          </Button>
        </div>
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Deine Sichten"
        beschreibung="Persönliche und freigegebene Filter."
      >
        {sichten.isLoading ? (
          <LadenZeile />
        ) : sichten.isError ? (
          <FehlerBlock
            fehler={sichten.error}
            fallback="Sichten konnten nicht geladen werden."
            onErneut={() => void sichten.refetch()}
          />
        ) : (sichten.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Noch keine Sichten."
            hinweis="Lege oben einen benannten Filter an."
          />
        ) : (
          <ul className="divide-y divide-linie">
            {sichten.data!.map((s) => {
              const filter = (s.filter ?? {}) as {
                projektId?: string;
                zustandGruppe?: string;
                nurOffen?: boolean;
              };
              const projektFuerLink = filter.projektId ?? s.projektId ?? undefined;
              return (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[14.5px] text-tinte">{s.name}</p>
                      {s.personId ? (
                        <Badge variant="neutral">persönlich</Badge>
                      ) : (
                        <Badge variant="success">geteilt</Badge>
                      )}
                    </div>
                    <p className="font-mono text-[11.5px] text-tinte3">
                      {projektName(filter.projektId ?? s.projektId)}
                      {filter.zustandGruppe
                        ? ` · ${filter.zustandGruppe}`
                        : ""}
                      {filter.nurOffen ? " · nur offen" : ""}
                    </p>
                  </div>
                  <Link
                    to="/aufgaben"
                    search={
                      projektFuerLink
                        ? { projekt: projektFuerLink }
                        : undefined
                    }
                    className="font-mono text-[12px] text-tinte2 underline-offset-2 hover:text-tinte hover:underline"
                  >
                    In Aufgaben öffnen
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
